import { type ComponentType, type ReactNode, useReducer, useMemo } from 'react';

import { useEventListener } from 'pkg/hooks/events';
import useComponentDidMount from 'pkg/hooks/useComponentDidMount';
import {
	RouterContext,
	initialState,
	initializeState,
	browserReducer,
	BROWSER_POP,
	BROWSER_PUSH,
	BROWSER_REPLACE,
	BROWSER_RESET,
	BROWSER_TRAVERSE,
} from 'pkg/router/store';

interface RouterEventDetail {
	location?: string;
	state?: Record<string, unknown>;
	fullReset?: boolean;
}

interface WithBrowserStateProps {
	children?: ReactNode;
}

export const withBrowserState = <P extends object>(
	WrappedComponent: ComponentType<P>
) => {
	return function WithBrowserState({
		children = null,
		...props
	}: WithBrowserStateProps & Omit<P, 'children'>) {
		let location = window.location.pathname;

		if (window.location.search.length > 0) {
			location += window.location.search;
		}

		// After a reload the entry keeps the position the router gave it.
		const landedAt = window.history.state?.position;
		const position = typeof landedAt === 'number' ? landedAt : 0;

		const [store, dispatch] = useReducer(
			browserReducer,
			initialState,
			initializeState([
				{
					location,
					type: null,
					direction: 0,
					position,
					state: {},
					search: {},
				},
			])
		);

		const contextValue = useMemo(
			() => ({
				store,
				dispatch,
				stateType: 'browser',
			}),
			[store, dispatch]
		);

		// A fresh page load leaves the history entry with no state, so popstate
		// later reads `state?.location` as undefined and falls back to '/'.
		// Stamp the landing location so native back returns here, not home.
		useComponentDidMount(() => {
			if (!window.history.state?.location) {
				window.history.replaceState(
					{
						...window.history.state,
						location,
						position,
						type: BROWSER_REPLACE,
					},
					'',
					location
				);
			}
		});

		const handleBrowserPopState = () => {
			const state = window.history.state;

			dispatch({
				skipMutations: true,
				type: BROWSER_TRAVERSE,
				payload: {
					location: state?.location || '/',
					position:
						typeof state?.position === 'number' ? state.position : undefined,
				},
			});
		};

		useEventListener('popstate', handleBrowserPopState);

		useEventListener('router:pop', (event: CustomEvent<RouterEventDetail>) => {
			const { location, state } = event.detail;

			dispatch({
				type: BROWSER_POP,
				payload: {
					location,
					state,
				},
			});
		});

		useEventListener('router:push', (event: CustomEvent<RouterEventDetail>) => {
			const { location, state } = event.detail;

			dispatch({
				type: BROWSER_PUSH,
				payload: {
					location,
					state,
				},
			});
		});

		useEventListener(
			'router:replace',
			(event: CustomEvent<RouterEventDetail>) => {
				const { location, state } = event.detail;

				dispatch({
					type: BROWSER_REPLACE,
					payload: {
						location,
						state,
					},
				});
			}
		);

		useEventListener(
			'router:reset',
			(event: CustomEvent<RouterEventDetail>) => {
				const { fullReset } = event.detail;

				dispatch({
					type: BROWSER_RESET,
					payload: {
						fullReset,
					},
				});
			}
		);

		return (
			<RouterContext.Provider value={contextValue}>
				<WrappedComponent {...(props as P)}>{children}</WrappedComponent>
			</RouterContext.Provider>
		);
	};
};
